import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "@/lib/router-compat";
import { soluciones } from "@/lib/soluciones";
import { Reveal } from "./Reveal";

// ---------------------------------------------------------------------------
// MODULES — "Siete piezas. Un solo sistema." Tabbed list + detail panel.
// Reuses the real soluciones data (titles, taglines, bullets).
// ---------------------------------------------------------------------------

const iconColors: Record<string, string> = {
  globe: "#A78BFA",
  star: "#F2B65A",
  message: "#34D399",
  inbox: "#A78BFA",
  search: "#E879F9",
  clock: "#F2B65A",
  bot: "#A78BFA",
};

export function HomeModules() {
  const navigate = useNavigate();
  const [cur, setCur] = useState(0);
  const mod = soluciones[cur];

  return (
    <section
      data-screen-label="Módulos"
      className="bg-[#0F0D14] border-t border-b border-[#221F2A]"
    >
      <div
        className="mx-auto px-6"
        style={{
          maxWidth: 1240,
          paddingTop: "clamp(80px,10vw,128px)",
          paddingBottom: "clamp(80px,10vw,128px)",
        }}
      >
        <Reveal>
          <div className="flex justify-between items-end gap-6 flex-wrap">
            <div>
              <div
                className="font-mono text-[13px] text-[#A78BFA]"
                style={{ letterSpacing: "0.08em" }}
              >
                06 — QUÉ INCLUYE
              </div>
              <h2
                className="mt-5 text-[#EEEDF2] font-medium"
                style={{
                  fontSize: "clamp(36px,4.8vw,64px)",
                  lineHeight: 1.02,
                  letterSpacing: "-0.025em",
                  maxWidth: 760,
                  textWrap: "balance" as never,
                }}
              >
                Siete piezas. Un solo sistema.
              </h2>
            </div>
            <button
              onClick={() => navigate("/soluciones")}
              className="font-medium text-[#A78BFA] hover:text-[#C4B5FD] transition-colors"
            >
              Producto en detalle →
            </button>
          </div>
        </Reveal>

        <div
          className="grid gap-6 mt-12"
          style={{
            gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,300px),1fr))",
          }}
        >
          {/* List */}
          <div className="flex flex-col border-t border-[#221F2A]">
            {soluciones.map((m, i) => {
              const active = i === cur;
              return (
                <button
                  key={m.slug}
                  onClick={() => setCur(i)}
                  className="grid items-center py-4 border-b border-[#221F2A] text-left transition-colors hover:text-[#EEEDF2]"
                  style={{
                    gridTemplateColumns: "34px 1fr auto",
                    gap: 10,
                    color: active ? "#EEEDF2" : "#9C98A6",
                  }}
                >
                  <span className="font-mono text-[12px] text-[#6E6A7A]">
                    {m.number}
                  </span>
                  <span className="text-[17px] font-medium">{m.title}</span>
                  <span style={{ color: iconColors[m.icon] ?? "#A78BFA" }}>
                    {active ? "▲" : "→"}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Detail */}
          <motion.div
            key={cur}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="bg-[#0A090D] border border-[#2A2633] rounded-2xl flex flex-col gap-4"
            style={{
              gridColumn: "span 2",
              minWidth: 0,
              padding: "clamp(24px,3vw,40px)",
            }}
          >
            <div className="flex items-center gap-2.5">
              <span className="font-mono text-[12px] text-[#6E6A7A]">
                {mod.number}
              </span>
              <span
                className="text-[12px] rounded-full px-2.5 py-0.5"
                style={{
                  background: "rgba(139,92,246,0.16)",
                  color: iconColors[mod.icon] ?? "#A78BFA",
                }}
              >
                {mod.shortTitle}
              </span>
            </div>
            <h3
              className="font-medium text-[#EEEDF2]"
              style={{
                fontSize: "clamp(32px,3.4vw,44px)",
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
              }}
            >
              {mod.title}
            </h3>
            <p className="text-[#BAB6C4] text-[18px] leading-[1.5]">
              {mod.tagline}
            </p>
            <div
              className="grid gap-px bg-[#221F2A] border border-[#221F2A] rounded-xl overflow-hidden mt-3"
              style={{
                gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
              }}
            >
              {mod.bullets.map((b) => (
                <div key={b.label} className="bg-[#110F17] p-5">
                  <h4 className="text-[15.5px] font-semibold text-[#EEEDF2]">
                    {b.label}
                  </h4>
                  <p
                    className="text-[14.5px] text-[#9C98A6] leading-[1.5] mt-1.5"
                    style={{ textWrap: "pretty" as never }}
                  >
                    {b.text}
                  </p>
                </div>
              ))}
            </div>
            <button
              onClick={() => navigate(`/que-hacemos/${mod.slug}`)}
              className="self-start mt-2 text-[#A78BFA] font-medium hover:text-[#C4B5FD] transition-colors"
            >
              Ver {mod.shortTitle} en detalle →
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
